import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AtSign, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * MentionPickerButton — Toolbar-Button für @handle-Autocomplete.
 *
 * Öffnet ein kleines Popover mit Suchfeld. Beim Auswählen ruft er
 * `onSelect(handle)` — der Parent soll `@handle ` an der Cursor-Position
 * ins Editor-Feld einfügen.
 *
 * Props:
 *  - onSelect(handle): Callback nach Auswahl
 *  - testId
 */
export default function MentionPickerButton({ onSelect, testId = "mention-picker" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (!query) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/mentions/search?q=${encodeURIComponent(query)}&limit=8`);
        if (!cancelled) setResults(data?.results || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open]);

  const pick = (item) => {
    setOpen(false);
    setQ("");
    setResults([]);
    if (typeof onSelect === "function") onSelect(item);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-btn`}
        title={t("feed_composer.mention_person_title")}
        className={`h-8 w-8 flex items-center justify-center rounded transition-colors ${
          open ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"
        }`}
      >
        <AtSign size={15} />
      </button>
      {open && (
        <div
          data-testid={`${testId}-panel`}
          className="absolute z-50 mt-1 left-0 w-72 max-w-[calc(100vw-1rem)] bg-white border border-[#0A0A0A] shadow-xl rounded-lg overflow-hidden"
        >
          <div className="p-2 border-b border-[#E5E5E5]">
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Person suchen…"
              data-testid={`${testId}-input`}
              className="w-full h-9 px-3 border border-[#E5E5E5] rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {busy && (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={14} className="animate-spin text-[#999]" />
              </div>
            )}
            {!busy && q && results.length === 0 && (
              <p className="text-xs text-[#999] px-3 py-3">Keine Treffer</p>
            )}
            {!busy && !q && (
              <p className="text-xs text-[#999] px-3 py-3">Tippe einen Namen…</p>
            )}
            {results.map((r) => (
              <button
                key={r.handle}
                type="button"
                onClick={() => pick(r)}
                data-testid={`${testId}-item-${r.handle}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F5F5F5] transition-colors"
              >
                {r.avatar_url ? (
                  <img src={r.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#E5E5E5] flex items-center justify-center text-xs font-bold text-[#666]">
                    {(r.display_name || r.handle || "?")[0].toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{r.display_name}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
